import type { ScoringProfile } from '../../../lib/profiles';
import { isA } from '../../../lib/graph/ontology';
import { matchEdgeKinds } from './edgeMatchers';

/**
 * Security profile — answers "what is exposed?"
 *
 * Combines simple per-node predicates (public exposure, unencrypted data
 * stores, weak IAM config) with chain rules that highlight paths from
 * public sources to sensitive data. The security profile is the most
 * useful one for Compass audits and ad-hoc risk reviews.
 *
 * Severity scale: critical > high > medium > low
 * - critical: broken compliance or active exposure (immediate action)
 * - high:     weak configuration that creates risk (prioritize fix)
 * - medium:   missing best practice (review on next audit)
 * - low:      informational / hygiene
 */
export const SecurityProfile: ScoringProfile = {
  id: 'network-security',
  name: 'Security',
  description: 'Surfaces exposed resources, unencrypted data, and public→data attack paths. Rules run against network-security and network-base field packs.',
  domain: 'network',
  visibility: {
    fieldPacks: ['network-base', 'network-security', 'network-tags'],
  },
  scoring: [
    // ─── Critical ──────────────────────────────────
    {
      id: 'sec.public-exposure',
      category: 'Exposure',
      label: 'Publicly exposed resource',
      description: 'Directly reachable from 0.0.0.0/0 via security group or NACL rule.',
      severity: 'critical',
      target: 'node',
      whenNode: (_n, p) => p.publicExposure === 'open',
    },
    {
      id: 'sec.public-data-path',
      category: 'Attack path',
      label: 'Public → Data path',
      description: 'Transitive network path from a public-facing resource to a data store. Highlights the full chain so you can see which hops need tightening.',
      severity: 'critical',
      target: 'both',
      type: 'chain',
      from: (_n, p) => p.publicExposure === 'open',
      // Match the walker kinds AND the universal types they collapse to in
      // `NetworkHydrator.EDGE_TYPE_MAP` — otherwise this chain silently
      // skips every real hydrated edge (whose `edge.type` is `data_flow`
      // or `association`, with the original kind on `props.infraEdgeKind`).
      via: matchEdgeKinds([
        'connects', 'depends_on', 'routes_to',
        'data_flow', 'association', 'sg_access',
        'reads-from', 'writes-to', 'routes-to',
      ]),
      to: (n) => isA(n.type, 'datastore'),
    },
    {
      id: 'sec.unencrypted-pii',
      category: 'Data protection',
      label: 'Unencrypted PII store',
      description: 'Data store containing PII is not encrypted at rest. SOC 2 CC6.1, HIPAA §164.312(a)(2)(iv), PCI DSS 3.4.',
      severity: 'critical',
      target: 'node',
      whenNode: (n, p) =>
        isA(n.type, 'datastore') && p.containsPII === true && p.encryptedAtRest === false,
    },
    {
      id: 'sec.publicly-accessible-db',
      category: 'Data protection',
      label: 'Publicly accessible database',
      description: 'Relational database has publiclyAccessible flag set. Almost always a misconfiguration.',
      severity: 'critical',
      target: 'node',
      whenNode: (n, p) => isA(n.type, 'database') && p.publiclyAccessible === true,
    },

    // ─── High ───────────────────────────────────────
    {
      id: 'sec.unencrypted-data',
      category: 'Data protection',
      label: 'Unencrypted data store',
      description: 'Data store is not encrypted at rest. Encryption should be enabled by default.',
      severity: 'high',
      target: 'node',
      whenNode: (n, p) =>
        isA(n.type, 'datastore') && p.encryptedAtRest === false && p.containsPII !== true,
    },
    {
      id: 'sec.wide-ingress',
      category: 'Access control',
      label: 'Overly permissive ingress',
      description: 'Security group rule allows inbound traffic from a wide CIDR (/8 or broader).',
      severity: 'high',
      target: 'node',
      whenNode: (n, p) => {
        if (n.type !== 'network_acl') return false;
        const rules = (p.ingressRules as Array<{ sourceCidr?: string }>) ?? [];
        return rules.some((r) => r.sourceCidr && /\/([0-9]|1[0-5])$/.test(r.sourceCidr));
      },
    },
    {
      id: 'sec.missing-flow-logs',
      category: 'Observability',
      label: 'Missing flow logs',
      description: 'Network boundary has no VPC Flow Logs enabled. No audit trail for network traffic.',
      severity: 'high',
      target: 'node',
      whenNode: (n, p) =>
        isA(n.type, 'network_boundary') && p.flowLogsEnabled === false,
    },

    // ─── Medium ─────────────────────────────────────
    {
      id: 'sec.mfa-not-enforced',
      category: 'Identity',
      label: 'MFA not enforced',
      description: 'IAM user or group can authenticate without multi-factor auth.',
      severity: 'medium',
      target: 'node',
      whenNode: (n, p) =>
        isA(n.type, 'user_identity') && p.mfaEnabled === false,
    },
    {
      id: 'sec.no-kms-rotation',
      category: 'Data protection',
      label: 'KMS key rotation disabled',
      description: 'Encryption key does not have automatic rotation enabled.',
      severity: 'medium',
      target: 'node',
      whenNode: (n, p) =>
        n.type === 'encryption_key' && p.rotationEnabled === false,
    },

    // ─── Low ────────────────────────────────────────
    {
      id: 'sec.untagged-resource',
      category: 'Hygiene',
      label: 'Untagged resource',
      description: 'Resource has no owner or environment tag. Makes incident response harder.',
      severity: 'low',
      target: 'node',
      whenNode: (_n, p) => {
        const tags = (p.tags as Record<string, string>) ?? {};
        return !tags.owner && !tags.env && !tags.Owner && !tags.Env;
      },
    },
  ],
};
